import UnsavedChangesNotice from '../components/ui/UnsavedChangesNotice';
import { useConfirmation } from '../contexts/ConfirmationContext';
import PageHeader from '../components/ui/PageHeader';
import React, { useState } from 'react';
import toast from 'react-hot-toast';

const UmbruchentfernerPage: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const confirm = useConfirmation();
  const [copied, setCopied] = useState('');
  const signature = JSON.stringify([inputText, outputText]);
  const dirty = !!(inputText || outputText) && signature !== copied;

  // Funktion zur Erkennung von Zwischenüberschriften
  // - max. 7 Wörter
  // - beginnt mit Großbuchstabe
  // - enthält keine Satzzeichen . ? ! : ;
  const isHeadingCandidate = (line: string): boolean => {
    const trimmed = line.trim();
    const words = trimmed.split(/\s+/);
    if (words.length > 7) return false;
    if (!/^[A-ZÄÖÜ]/.test(trimmed)) return false;
    if (/[.!?:;]/.test(trimmed)) return false;
    return true;
  };

  const removeLineBreaks = () => {
    // 1. Absätze anhand von Doppel-Umbrüchen trennen
    const paragraphs = inputText.split(/\r?\n\s*\r?\n/);

    // 2. Innerhalb jedes Absatzes Zeilenweises Mergen, 
    //    außer es handelt sich um eine erkannte Überschrift
    const cleanedParas = paragraphs.map(p => {
      const lines = p.split(/\r?\n/).map(l => l.trim()).filter(l => l.length > 0);
      const out: string[] = [];
      const firstIsHeading = lines.length > 0 && isHeadingCandidate(lines[0]);
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (i === 0) {
          out.push(line);
        } else {
          const prev = out[out.length - 1];
          const prevEndsWithPunc = /[.?!:]$/.test(prev);
          const currIsHeading = isHeadingCandidate(line);
          const breakOnFirst = (i === 1 && firstIsHeading);
          
          if (prevEndsWithPunc || currIsHeading || breakOnFirst) {
            // Wenn vorige Zeile Satzende, oder aktuelle Zeile Heading,
            // oder wir gerade nach einem Heading die erste Zeile mergen würden:
            out.push(line);
          } else {
            // Innerhalb eines Satzes zusammenfügen
            out[out.length - 1] = prev + ' ' + line;
          }
        }
      }
      return out.join('\n');
    });

    // 3. Absätze wieder mit Doppel-Umbruch zusammenfügen
    const output = cleanedParas.join('\n\n');
    setOutputText(output);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(outputText);
      setCopied(signature);
      toast.success('Text in Zwischenablage kopiert!');
    } catch (err) {
      console.error('Fehler beim Kopieren:', err);
      toast.error('Fehler beim Kopieren in die Zwischenablage');
    }
  };

  const clearText = async () => {
    if (dirty && !(await confirm({ title: 'Texte verwerfen?', description: 'Eingabe und Ergebnis werden entfernt. Kopiere dein Ergebnis bei Bedarf zuerst.', confirmLabel: 'Texte verwerfen', danger: true }))) return;
    setInputText('');
    setOutputText('');
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <PageHeader title={<>Umbruchentferner</>} description={<>Entfernt Zeilenumbrüche innerhalb von Sätzen und behält Überschriften bei</>} />
        </div>
        
        <div className="px-6 py-8">
          <div className="space-y-6">
            <UnsavedChangesNotice dirty={dirty} localOnly />
            {/* Eingabe-Bereich */}
            <div>
              <label htmlFor="inputText" className="block text-sm font-medium text-gray-700 mb-2">
                Text aus PDF einfügen:
              </label>
              <textarea
                id="inputText"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Hier den Text aus PDF einfügen..."
                className="w-full h-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical"
              />
            </div>

            {/* Buttons */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={removeLineBreaks}
                disabled={!inputText.trim()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Umbrüche bereinigen
              </button>
              
              {outputText && (
                <button
                  onClick={copyToClipboard}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  In Zwischenablage kopieren
                </button>
              )}
              
              <button
                onClick={clearText}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Texte verwerfen
              </button>
            </div>

            {/* Ausgabe-Bereich */}
            <div>
              <label htmlFor="outputText" className="block text-sm font-medium text-gray-700 mb-2">
                Ergebnis:
              </label>
              <textarea
                id="outputText"
                value={outputText}
                readOnly
                placeholder="Hier erscheint der bereinigte Text..."
                className="w-full h-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 resize-vertical"
              />
            </div>

            {/* Info-Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Wie funktioniert der Umbruchentferner?
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Erkennt automatisch Überschriften (max. 7 Wörter, beginnt mit Großbuchstabe, keine Satzzeichen)</li>
                      <li>Entfernt Zeilenumbrüche innerhalb von Sätzen</li>
                      <li>Behält Absätze und Überschriften bei</li>
                      <li>Perfekt für PDF-Text, der ungewollte Zeilenumbrüche enthält</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UmbruchentfernerPage;
